import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'What is AI lead follow-up and how does it work?',
    answer: 'AI lead follow-up is an automated system that contacts new leads immediately after they inquire — via text, email, or both — and continues nurturing them with personalized messages until they book or respond. It uses artificial intelligence to craft natural, human-sounding messages tailored to each lead\'s inquiry, so no lead ever slips through the cracks.'
  },
  {
    question: 'How fast does the AI follow up with new leads?',
    answer: 'Our AI responds to new leads within 60 seconds of their inquiry, 24 hours a day, 7 days a week. Research shows that leads contacted within the first 5 minutes are 21x more likely to convert than those contacted after 30 minutes. Speed is everything, and our AI never sleeps.'
  },
  {
    question: 'Can the AI follow up via text message and email?',
    answer: 'Yes. Our AI lead follow-up system sends personalized SMS text messages and emails, whichever channel your lead prefers. Text messages have a 98% open rate compared to 20% for email, so we prioritize SMS while using email as a powerful secondary touchpoint.'
  },
  {
    question: 'Will the follow-up messages sound robotic or generic?',
    answer: 'Not at all. Our AI crafts messages that reference the specific service the lead inquired about, their name, and the context of their request. Messages are conversational and natural — most leads have no idea they\'re talking to an AI until they\'re already booked on your calendar.'
  },
  {
    question: 'How many follow-up attempts does the AI make?',
    answer: 'You control the follow-up sequence. By default, the AI sends a series of messages over 7–14 days — an immediate response, a follow-up the next day, then periodic check-ins — stopping automatically once the lead responds or books. You can customize the cadence and number of touchpoints to fit your business.'
  },
  {
    question: 'Does the AI lead follow-up integrate with my CRM?',
    answer: 'Yes. Our system integrates with popular CRMs including HubSpot, Salesforce, GoHighLevel, and more. Lead data, conversation history, and booking status are automatically synced so your team always has full context without manual data entry.'
  },
  {
    question: 'What types of businesses benefit most from AI lead follow-up?',
    answer: 'Any service-based business that generates leads online benefits enormously — home services (HVAC, plumbing, roofing, landscaping), contractors, medical and dental practices, real estate agents, law firms, and more. If you\'re getting leads but struggling to convert them, AI follow-up is a game-changer.'
  },
  {
    question: 'Can I see the conversations the AI is having with my leads?',
    answer: 'Absolutely. You get a full dashboard showing every conversation, response rate, booking rate, and lead status in real time. You can jump into any conversation at any time to take over manually, and the AI will step aside seamlessly.'
  }
];

const stats = [
  { value: '60 sec', label: 'Average response time to new leads' },
  { value: '21x', label: 'More likely to convert when contacted in 5 min' },
  { value: '78%', label: 'Of leads buy from the first company to respond' },
  { value: '40%', label: 'Average increase in lead conversion rate' },
];

const steps = [
  {
    number: '01',
    title: 'Lead Comes In',
    description: 'A potential customer fills out your form, calls, or messages you from any source — website, Facebook, Google, or referral. The AI detects the new lead instantly.',
    icon: 'ri-user-add-line'
  },
  {
    number: '02',
    title: 'Instant AI Response',
    description: 'Within 60 seconds, the AI sends a personalized text and email referencing exactly what the lead asked about. No templates — real, contextual messages that feel human.',
    icon: 'ri-send-plane-line'
  },
  {
    number: '03',
    title: 'Smart Nurture Sequence',
    description: 'If the lead doesn\'t respond, the AI follows up at strategic intervals over the next 7–14 days with varied messages — never spammy, always relevant and helpful.',
    icon: 'ri-loop-right-line'
  },
  {
    number: '04',
    title: 'Lead Responds',
    description: 'When the lead replies, the AI continues the conversation, answers questions, and guides them toward booking — all automatically, all in real time.',
    icon: 'ri-chat-check-line'
  },
  {
    number: '05',
    title: 'Appointment Booked',
    description: 'The AI books the appointment directly onto your calendar, sends confirmation messages, and hands off to your team with full conversation context. Done.',
    icon: 'ri-calendar-check-line'
  }
];

const features = [
  {
    icon: 'ri-flashlight-line',
    title: 'Instant Lead Response',
    description: 'Contact every new lead within 60 seconds, day or night. Never lose a job to a competitor who picked up the phone first.'
  },
  {
    icon: 'ri-message-3-line',
    title: 'SMS + Email Outreach',
    description: 'Reach leads on the channels they actually use. SMS gets 98% open rates; email provides a professional paper trail. We use both.'
  },
  {
    icon: 'ri-brain-line',
    title: 'Personalized AI Messages',
    description: 'Every message references the lead\'s name, their specific inquiry, and your business — not a generic template that screams "automation."'
  },
  {
    icon: 'ri-repeat-line',
    title: 'Multi-Touch Follow-Up',
    description: 'A single follow-up isn\'t enough. Our AI sends a strategic sequence of messages over days and weeks until the lead responds or opts out.'
  },
  {
    icon: 'ri-calendar-2-line',
    title: 'Automatic Booking',
    description: 'When a lead is ready, the AI books them directly onto your calendar — no back-and-forth, no phone tag, no missed opportunities.'
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Real-Time Dashboard',
    description: 'See every conversation, track response rates, and monitor your pipeline in a clean dashboard. Know exactly where every lead stands.'
  },
  {
    icon: 'ri-links-line',
    title: 'CRM Integration',
    description: 'Syncs with HubSpot, GoHighLevel, Salesforce, and more. Lead data flows automatically — no manual entry, no duplicate records.'
  },
  {
    icon: 'ri-user-voice-line',
    title: 'Human Handoff',
    description: 'Jump into any conversation at any time. The AI steps aside the moment you take over, keeping the experience seamless for your lead.'
  }
];

export default function AILeadFollowUp() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="AI Lead Follow-Up for Small Businesses | Dino Nuggets"
        description="Stop losing leads to slow follow-up. Our AI lead follow-up system contacts every new lead within 60 seconds via text and email, nurtures them automatically, and books appointments — 24/7."
        canonical="/services/ai-lead-follow-up"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Lead Follow-Up Service',
              description: 'Automated AI lead follow-up system that contacts new leads within 60 seconds via SMS and email, nurtures them with personalized messages, and books appointments automatically.',
              provider: {
                '@type': 'Organization',
                name: 'Dino Nuggets',
                url: import.meta.env.VITE_SITE_URL || 'https://dinonuggets.ai'
              },
              areaServed: 'United States',
              availableChannel: {
                '@type': 'ServiceChannel',
                serviceUrl: `${import.meta.env.VITE_SITE_URL || 'https://dinonuggets.ai'}/services/ai-lead-follow-up`
              }
            }
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer
                }
              }))
            }
          }
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Section */}
        <section className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/ai-lead-hero-001.jpg"
              alt="AI Lead Follow-Up Technology"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

          <div className="relative w-full max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-flashlight-line"></i>
              Responds in under 60 seconds
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              AI Lead Follow-Up That Never Sleeps
            </h1>
            <p className="text-xl text-white/85 mb-10 leading-relaxed max-w-3xl mx-auto">
              78% of customers buy from the first business to respond. Our AI lead follow-up system contacts every new lead within 60 seconds — via text and email — and keeps nurturing them until they book. Automatically.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Start Following Up Automatically
              </button>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                See How It Works
              </button>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-secondary py-12 px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm leading-snug">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* What Is It */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                What Is AI Lead Follow-Up?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                AI lead follow-up is an automated system that instantly contacts every new lead your business receives — whether they came from your <Link to="/services/ai-chatbot" className="text-primary hover:underline font-medium">website chatbot</Link>, Google, Facebook, or a referral — and continues nurturing them with personalized messages until they respond, book, or opt out.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                For small businesses, the biggest source of lost revenue isn't a lack of leads — it's slow or inconsistent follow-up. Studies show that <strong>78% of customers buy from the first company to respond</strong>, yet the average small business takes over 47 hours to follow up with a new lead. By then, your competitor has already booked the job.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our AI lead follow-up service eliminates that gap entirely. The moment a lead comes in, the AI fires off a personalized text and email within 60 seconds — then continues following up at strategic intervals until you get a response. Once ready, the lead can be <Link to="/ai-appointment-scheduling" className="text-primary hover:underline font-medium">booked automatically</Link> onto your calendar. It's like having a dedicated sales rep working around the clock, without the salary.
              </p>
            </div>

            {/* How It Works - Steps */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-10">
                How AI Lead Follow-Up Works
              </h2>
              <div className="space-y-6">
                {steps.map((step) => (
                  <div key={step.number} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl">
                      <i className={`${step.icon} text-2xl text-primary`}></i>
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-bold text-primary tracking-widest">{step.number}</span>
                        <h3 className="font-semibold text-lg text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features Grid */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Everything You Need to Convert More Leads
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Our AI lead follow-up system is built specifically for service-based small businesses. Here's what's included:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {features.map((feature) => (
                  <div key={feature.title} className="border border-gray-200 p-6 rounded-lg hover:border-primary/40 transition-colors">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                      <i className={`${feature.icon} text-xl text-primary`}></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Who It's For */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-10 rounded-2xl">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Built for Service-Based Small Businesses
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                If your business generates leads online and relies on converting those leads into booked jobs or appointments, AI lead follow-up will transform your revenue. Industries we serve include:
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-home-gear-line', label: 'HVAC & Home Services' },
                  { icon: 'ri-tools-line', label: 'Contractors & Builders' },
                  { icon: 'ri-plant-line', label: 'Landscaping & Lawn Care' },
                  { icon: 'ri-building-2-line', label: 'Roofing Companies' },
                  { icon: 'ri-heart-pulse-line', label: 'Medical & Dental Practices' },
                  { icon: 'ri-home-4-line', label: 'Real Estate Agents' },
                  { icon: 'ri-scales-3-line', label: 'Law Firms' },
                  { icon: 'ri-car-line', label: 'Auto Services' },
                  { icon: 'ri-scissors-line', label: 'Salons & Spas' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <div className="w-8 h-8 flex items-center justify-center text-primary">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <span className="text-gray-800 text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Why Speed Matters */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Why Speed-to-Lead Is Everything
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The data is clear: the speed at which you respond to a new lead is the single biggest factor in whether you win or lose that job. A Harvard Business Review study found that companies that respond to leads within an hour are <strong>7 times more likely to qualify the lead</strong> than those that wait even 60 minutes.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                For small businesses, this is a massive problem. You're busy running jobs, managing your team, and handling the day-to-day. You can't drop everything every time a lead comes in — especially at 9pm on a Tuesday. So leads sit. They wait. And they call your competitor.
              </p>
              <p className="text-gray-700 leading-relaxed mb-8">
                AI lead follow-up solves this completely. The AI responds in under 60 seconds, every single time, with a message that feels personal and relevant. Your lead feels heard and valued immediately — and you didn't have to lift a finger.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-red-50 border border-red-100 p-6 rounded-xl text-center">
                  <div className="text-3xl font-bold text-red-500 mb-2">47 hrs</div>
                  <div className="text-sm text-gray-600">Average small business lead response time</div>
                </div>
                <div className="bg-amber-50 border border-amber-100 p-6 rounded-xl text-center">
                  <div className="text-3xl font-bold text-amber-500 mb-2">5 min</div>
                  <div className="text-sm text-gray-600">Optimal window to contact a new lead</div>
                </div>
                <div className="bg-primary/5 border border-primary/20 p-6 rounded-xl text-center">
                  <div className="text-3xl font-bold text-primary mb-2">60 sec</div>
                  <div className="text-sm text-gray-600">Our AI lead follow-up response time</div>
                </div>
              </div>
            </div>

            {/* Related Services */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Pair AI Lead Follow-Up with These Services
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Get even more out of your lead pipeline by combining AI follow-up with our other automation tools:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <Link
                  to="/services/ai-chatbot"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                >
                  <i className="ri-chat-smile-3-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    AI Chatbot
                  </h3>
                  <p className="text-sm text-gray-600">
                    Capture leads from your website 24/7 and feed them directly into your follow-up pipeline.
                  </p>
                </Link>

                <Link
                  to="/services/ai-receptionist"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                >
                  <i className="ri-customer-service-2-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    AI Receptionist
                  </h3>
                  <p className="text-sm text-gray-600">
                    Handle inbound calls and route hot leads directly into your automated follow-up sequence.
                  </p>
                </Link>

                <Link
                  to="/ai-appointment-scheduling"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                >
                  <i className="ri-calendar-check-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    Appointment Scheduling
                  </h3>
                  <p className="text-sm text-gray-600">
                    Let the AI book appointments automatically once a lead is ready — no phone tag needed.
                  </p>
                </Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i
                        className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${
                          openFaqIndex === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Closing CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">
                Stop Losing Leads to Slow Follow-Up
              </h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Every lead that doesn't hear from you in the first 5 minutes is a lead your competitor is booking. Let our AI follow up instantly — so you never lose another job to slow response time.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="inline-block bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Get AI Lead Follow-Up Today
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />

        <ServiceModal
          isOpen={isServiceModalOpen}
          onClose={() => setIsServiceModalOpen(false)}
          title={selectedService}
        />

        <IndustriesModal
          isOpen={isIndustriesModalOpen}
          onClose={() => setIsIndustriesModalOpen(false)}
        />

        <HowItWorksModal
          isOpen={isHowItWorksModalOpen}
          onClose={() => setIsHowItWorksModalOpen(false)}
        />

        <CalendarModal
          isOpen={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
        />
      </div>
    </>
  );
}
